import { Dispatch, SetStateAction, useMemo, useState } from "react";
import {
  ArrowTopRightOnSquareIcon,
  ArrowUturnLeftIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
  PrinterIcon,
  ShoppingCartIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import Modal from "../../layouts/Modal";
import Tooltip from "../shared/Tooltip";
import StatusPill, { StatusPillColour } from "../shared/StatusPill";
import SearchPaletteTypePill from "../shared/Inputs/SearchPaletteTypePill";
import { SearchPaletteItemType } from "../shared/Inputs/SearchPaletteTypes";
import useWorkOrderParts from "../../data/useWorkOrderParts";
import useWorkOrderMutations from "../../data/useWorkOrderMutations";
import useBaskets from "../../data/useBaskets";
import printWorkOrderParts from "./printWorkOrderParts";
import openInNewTab from "../../utils/openInNewTab";
import classNames from "../../utils/classNames";
import {
  IWorkOrder,
  IWorkOrderPart,
  WorkOrderItemRejectionStatus,
  WorkOrderItemType,
  WorkOrderPriority,
} from "../../models/WorkOrder";
import { PartStatus } from "../../models/Part";

interface WorkOrderPartsModalProps {
  workOrder: IWorkOrder | null;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

export default function WorkOrderPartsModal({ workOrder, open, setOpen }: WorkOrderPartsModalProps) {
  const workOrderId = workOrder?.id ?? null;
  const partsQuery = useWorkOrderParts(workOrderId, open);
  const parts = partsQuery.data?.data ?? [];

  const { updateItemRejection, updateItemPrice, removeItem } = useWorkOrderMutations();
  const { addItemsMultiple } = useBaskets("WorkOrderPartsModal");

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [priceDrafts, setPriceDrafts] = useState<Record<string, string>>({});
  const [pendingRejection, setPendingRejection] = useState<{
    part: IWorkOrderPart;
    status: WorkOrderItemRejectionStatus;
  } | null>(null);
  const [reasonText, setReasonText] = useState("");

  const totalItems = parts.length;
  const totalValue = useMemo(
    () => parts.reduce((sum, p) => sum + (p.price ?? 0), 0),
    [parts]
  );
  const rejectedCount = useMemo(
    () => parts.filter((p) => p.rejectionStatus !== WorkOrderItemRejectionStatus.None).length,
    [parts]
  );

  const nonRejectedParts = useMemo(
    () => parts.filter((p) => p.rejectionStatus === WorkOrderItemRejectionStatus.None),
    [parts]
  );

  const allNonRejectedSelected =
    nonRejectedParts.length > 0 &&
    nonRejectedParts.every((p) => selectedIds.has(p.workOrderItemId));

  const toggleAllNonRejected = () => {
    if (allNonRejectedSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(nonRejectedParts.map((p) => p.workOrderItemId)));
    }
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submitRejection = (
    part: IWorkOrderPart,
    status: WorkOrderItemRejectionStatus,
    reason: string | null
  ) => {
    if (!workOrderId) return;
    updateItemRejection.mutate({
      workOrderId,
      itemId: part.workOrderItemId,
      body: { rejectionStatus: status, reason },
    });
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(part.workOrderItemId);
      return next;
    });
  };

  // QC and scrap require a reason, so open the prompt first; undo applies immediately.
  const requestReject = (part: IWorkOrderPart, status: WorkOrderItemRejectionStatus) => {
    if (status === WorkOrderItemRejectionStatus.None) {
      submitRejection(part, status, null);
      return;
    }
    setReasonText("");
    setPendingRejection({ part, status });
  };

  const confirmRejection = () => {
    if (!pendingRejection) return;
    const reason = reasonText.trim();
    if (!reason) return;
    submitRejection(pendingRejection.part, pendingRejection.status, reason);
    setPendingRejection(null);
    setReasonText("");
  };

  const handleRemove = (part: IWorkOrderPart) => {
    if (!workOrderId) return;
    removeItem.mutate({ workOrderId, itemId: part.workOrderItemId });
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(part.workOrderItemId);
      return next;
    });
  };

  const handlePriceBlur = (part: IWorkOrderPart) => {
    if (!workOrderId) return;
    const draft = priceDrafts[part.workOrderItemId];
    if (draft === undefined) return;
    const parsed = draft === "" ? null : Number(draft);
    if (parsed !== null && Number.isNaN(parsed)) return;
    if (parsed === (part.price ?? null)) return;
    updateItemPrice.mutate({
      workOrderId,
      itemId: part.workOrderItemId,
      body: { price: parsed },
    });
  };

  const handlePrint = () => {
    if (parts.length === 0) return;
    printWorkOrderParts(parts, {
      orderNumber: workOrder?.orderNumber,
      title: workOrder?.title,
      priority: workOrder?.priority ?? null,
    });
  };

  const handleAddToBasket = () => {
    const linkedIds = parts
      .filter((p) => selectedIds.has(p.workOrderItemId) && p.type === WorkOrderItemType.Stock)
      .map((p) => p.linkedId);
    if (linkedIds.length === 0) return;
    addItemsMultiple.mutate(linkedIds);
  };

  const priorityLabel = workOrder ? getPriorityLabel(workOrder.priority) : "";
  const priorityColour = workOrder ? getPriorityColour(workOrder.priority) : StatusPillColour.Neutral;

  const pendingIsScrap = pendingRejection?.status === WorkOrderItemRejectionStatus.Scrapped;

  return (
    <>
    <Modal open={open} setOpen={setOpen} width="max-w-7xl">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">
          {workOrder ? `${workOrder.orderNumber} — ${workOrder.title}` : "Work Order"}
        </h2>
        <div className="flex items-center gap-2">
          {workOrder && (
            <StatusPill
              text={priorityLabel}
              colour={priorityColour}
              icon={workOrder.priority === WorkOrderPriority.Critical ? ExclamationTriangleIcon : undefined}
            />
          )}
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <StatCard label="Total Items" value={String(totalItems)} />
        <StatCard label="Total Value" value={formatCurrency(totalValue)} />
        <StatCard
          label="Rejected"
          value={String(rejectedCount)}
          valueClass={rejectedCount > 0 ? "text-red-600" : undefined}
        />
      </div>

      <div className="flex items-center justify-between mb-2">
        <p className="text-sm text-gray-600">
          {totalItems} {totalItems === 1 ? "item" : "items"}
          {selectedIds.size > 0 && <> · {selectedIds.size} selected</>}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={totalItems === 0}
            onClick={handlePrint}
            className={classNames(
              "inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium shadow-sm",
              totalItems === 0
                ? "border-gray-200 text-gray-300 cursor-not-allowed"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
            )}
          >
            <PrinterIcon className="h-4 w-4" />
            Print
          </button>
          <button
            type="button"
            disabled={nonRejectedParts.length === 0}
            onClick={toggleAllNonRejected}
            className={classNames(
              "inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium shadow-sm",
              nonRejectedParts.length === 0
                ? "border-gray-200 text-gray-300 cursor-not-allowed"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
            )}
          >
            {allNonRejectedSelected ? "Deselect all" : "Select all non-rejected"}
          </button>
          <button
            type="button"
            disabled={selectedIds.size === 0 || addItemsMultiple.isLoading}
            onClick={handleAddToBasket}
            className={classNames(
              "inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium shadow-sm",
              selectedIds.size === 0
                ? "bg-purple-200 text-white cursor-not-allowed"
                : "bg-purple-600 text-white hover:bg-purple-700"
            )}
          >
            <ShoppingCartIcon className="h-4 w-4" />
            Add to basket{selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left text-gray-600">
              <th className="px-3 py-2 w-10">
                <input
                  type="checkbox"
                  aria-label="Select all non-rejected parts"
                  checked={allNonRejectedSelected}
                  onChange={toggleAllNonRejected}
                  className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
              </th>
              <th className="px-3 py-2 font-medium">Item</th>
              <th className="px-3 py-2 font-medium">Tag No.</th>
              <th className="px-3 py-2 font-medium">Account</th>
              <th className="px-3 py-2 font-medium">Part Category</th>
              <th className="px-3 py-2 font-medium">Location</th>
              <th className="px-3 py-2 font-medium">Price</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {partsQuery.isLoading && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-gray-500">
                  Loading parts…
                </td>
              </tr>
            )}
            {!partsQuery.isLoading && parts.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-gray-500">
                  No parts on this work order.
                </td>
              </tr>
            )}
            {parts.map((part) => {
              const isRejected = part.rejectionStatus !== WorkOrderItemRejectionStatus.None;
              const isSelected = selectedIds.has(part.workOrderItemId);
              const priceValue =
                priceDrafts[part.workOrderItemId] ??
                (part.price === null || part.price === undefined ? "0" : String(part.price));

              return (
                <tr
                  key={part.workOrderItemId}
                  className={classNames(
                    "transition-colors",
                    isRejected ? "bg-red-50" : "hover:bg-gray-50"
                  )}
                >
                  <td className="px-3 py-2 align-middle">
                    <input
                      type="checkbox"
                      disabled={isRejected}
                      checked={isSelected}
                      onChange={() => toggleOne(part.workOrderItemId)}
                      className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 disabled:opacity-40"
                    />
                  </td>
                  <td className="px-3 py-2 align-middle">
                    <div className="flex items-center gap-2">
                      <SearchPaletteTypePill
                        type={mapWorkOrderItemTypeToSearchPaletteItemType(part.type)}
                        nested
                        className="shrink-0"
                      />
                      <span className="font-medium text-gray-900">{part.title}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 align-middle text-gray-700">{part.tagNumber ?? "-"}</td>
                  <td className="px-3 py-2 align-middle text-gray-700">{part.accountName ?? "-"}</td>
                  <td className="px-3 py-2 align-middle text-gray-700">{part.partCategoryName ?? "-"}</td>
                  <td className="px-3 py-2 align-middle text-gray-700">{part.warehouseLocation ?? "-"}</td>
                  <td className="px-3 py-2 align-middle">
                    <div className="flex items-center gap-1">
                      <span className="text-gray-500">£</span>
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={priceValue}
                        onChange={(e) =>
                          setPriceDrafts((prev) => ({ ...prev, [part.workOrderItemId]: e.target.value }))
                        }
                        onBlur={() => handlePriceBlur(part)}
                        className="w-24 rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                      />
                    </div>
                  </td>
                  <td className="px-3 py-2 align-middle">
                    <div className="flex flex-col items-start gap-1">
                      {part.rejectionStatus === WorkOrderItemRejectionStatus.QC && (
                        <StatusPill text="Rejected · QC" colour={StatusPillColour.Yellow} />
                      )}
                      {part.rejectionStatus === WorkOrderItemRejectionStatus.Scrapped && (
                        <StatusPill text="Rejected · Scrapped" colour={StatusPillColour.Red} />
                      )}
                      {part.partStatus !== null && part.partStatus !== undefined ? (
                        <StatusPill
                          text={getPartStatusLabel(part.partStatus)}
                          colour={getPartStatusColour(part.partStatus)}
                        />
                      ) : (
                        part.rejectionStatus === WorkOrderItemRejectionStatus.None && (
                          <span className="text-gray-400">-</span>
                        )
                      )}
                      {isRejected && part.rejectionReason && (
                        <span className="text-xs text-gray-500 italic max-w-[200px] truncate" title={part.rejectionReason}>
                          “{part.rejectionReason}”
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 align-middle">
                    <div className="flex items-center justify-end gap-3">
                      <Tooltip content="Open">
                        <ArrowTopRightOnSquareIcon
                          onClick={() => openInNewTab(getItemLink(part))}
                          className="h-5 w-5 text-gray-500 hover:text-gray-700 cursor-pointer"
                        />
                      </Tooltip>
                      {isRejected ? (
                        <Tooltip content="Undo rejection">
                          <ArrowUturnLeftIcon
                            onClick={() => requestReject(part, WorkOrderItemRejectionStatus.None)}
                            className="h-5 w-5 text-amber-600 hover:text-amber-700 cursor-pointer"
                          />
                        </Tooltip>
                      ) : (
                        <>
                          <Tooltip content="Reject &amp; send to QC">
                            <NoSymbolIcon
                              onClick={() => requestReject(part, WorkOrderItemRejectionStatus.QC)}
                              className="h-5 w-5 text-amber-600 hover:text-amber-700 cursor-pointer"
                            />
                          </Tooltip>
                          <Tooltip content="Reject &amp; scrap out">
                            <TrashIcon
                              onClick={() => requestReject(part, WorkOrderItemRejectionStatus.Scrapped)}
                              className="h-5 w-5 text-red-600 hover:text-red-700 cursor-pointer"
                            />
                          </Tooltip>
                        </>
                      )}
                      <Tooltip content="Remove from work order">
                        <XMarkIcon
                          onClick={() => handleRemove(part)}
                          className="h-5 w-5 text-gray-400 hover:text-gray-700 cursor-pointer"
                        />
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Modal>

    <Modal open={pendingRejection !== null} setOpen={() => setPendingRejection(null)} width="max-w-lg">
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">
          {pendingIsScrap ? "Reject & scrap out" : "Reject & send to QC"}
        </h2>
        <p className="text-sm text-gray-600">
          {pendingIsScrap ? (
            <>This will scrap out <span className="font-medium text-gray-900">{pendingRejection?.part.title}</span> and remove it from stock. Please give a reason.</>
          ) : (
            <>This will send <span className="font-medium text-gray-900">{pendingRejection?.part.title}</span> to QC. Please give a reason.</>
          )}
        </p>
        <textarea
          autoFocus
          rows={3}
          value={reasonText}
          onChange={(e) => setReasonText(e.target.value)}
          placeholder="Reason for rejection…"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
        />
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={() => setPendingRejection(null)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!reasonText.trim() || updateItemRejection.isLoading}
            onClick={confirmRejection}
            className={classNames(
              "rounded-md px-3 py-2 text-sm font-semibold text-white",
              !reasonText.trim()
                ? "bg-gray-300 cursor-not-allowed"
                : pendingIsScrap
                ? "bg-red-600 hover:bg-red-700"
                : "bg-amber-600 hover:bg-amber-700"
            )}
          >
            {pendingIsScrap ? "Scrap out" : "Send to QC"}
          </button>
        </div>
      </div>
    </Modal>
    </>
  );
}

function StatCard({ label, value, valueClass }: { label: string; value: string; valueClass?: string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
      <p className={classNames("mt-1 text-2xl font-semibold text-gray-900", valueClass)}>{value}</p>
    </div>
  );
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(amount);
}

function getItemLink(part: IWorkOrderPart): string {
  switch (part.type) {
    case WorkOrderItemType.Order:
      return `/stock/${part.linkedId}`;
    case WorkOrderItemType.Vehicle:
      return `/vehicles/${part.linkedId}`;
    case WorkOrderItemType.Stock:
      return `/stock/${part.linkedId}`;
    case WorkOrderItemType.Lead:
      return `/leads/${part.linkedId}`;
    default:
      return `/stock/${part.linkedId}`;
  }
}

function mapWorkOrderItemTypeToSearchPaletteItemType(type: WorkOrderItemType): SearchPaletteItemType {
  switch (type) {
    case WorkOrderItemType.Order:
      return "Order";
    case WorkOrderItemType.Vehicle:
      return "Vehicle";
    case WorkOrderItemType.Stock:
      return "Stock";
    case WorkOrderItemType.Lead:
      return "Lead";
    default:
      return "Vehicle";
  }
}

function getPriorityLabel(priority: WorkOrderPriority): string {
  switch (priority) {
    case WorkOrderPriority.Low:
      return "Low";
    case WorkOrderPriority.Medium:
      return "Medium";
    case WorkOrderPriority.High:
      return "High";
    case WorkOrderPriority.Critical:
      return "Critical";
    default:
      return "Unknown";
  }
}

function getPriorityColour(priority: WorkOrderPriority): StatusPillColour {
  switch (priority) {
    case WorkOrderPriority.Low:
      return StatusPillColour.Green;
    case WorkOrderPriority.Medium:
      return StatusPillColour.Yellow;
    case WorkOrderPriority.High:
      return StatusPillColour.Orange;
    case WorkOrderPriority.Critical:
      return StatusPillColour.Red;
    default:
      return StatusPillColour.Neutral;
  }
}

function getPartStatusLabel(status: PartStatus): string {
  return PartStatus[status] ?? "Unknown";
}

function getPartStatusColour(status: PartStatus): StatusPillColour {
  switch (status) {
    case PartStatus.Dismantled:
      return StatusPillColour.Green;
    case PartStatus.QC:
      return StatusPillColour.Yellow;
    case PartStatus.Damaged:
      return StatusPillColour.Orange;
    case PartStatus.Missing:
    case PartStatus.Deleted:
      return StatusPillColour.Red;
    case PartStatus.Sold:
      return StatusPillColour.Blue;
    default:
      return StatusPillColour.Neutral;
  }
}
